import type { PathTranslations } from 'service/localization/getPathTranslations';
import { SearchService } from 'service/search/searchService';
import type { SearchClient } from 'algoliasearch';
import type { FilterTopic } from 'components/FilterMenu/types';
import type { SearchResultCard } from 'server/repository/algolia/mapper';
import { mapAlgoliaRecipeToSearchCard } from 'server/repository/algolia/mapper/AlgoliaRecipeToSearchCard/AlgoliaRecipeToSearchCard';
import { mapAlgoliaTipsToSearchCard } from 'server/repository/algolia/mapper/AlgoliaTipsToSearchCard/AlgoliaTipsToSearchCard';
import { mapAlogliaArticleToSearchCard } from 'server/repository/algolia/mapper/AlogliaArticleToSearchCard/AlogliaArticleToSearchCard';
import { mapAlogliaProductToSearchCard } from 'server/repository/algolia/mapper/AlogliaProductToSearchCard/AlogliaProductToSearchCard';
import { mapGlobalSearchResultsToCards } from 'server/repository/algolia/mapper/GlobalSearchResultsToCards/GlobalSearchResultsToCards';
import type {
  FormattedArticle,
  FormattedProduct,
  FormattedRecipes,
  FormattedTipsAndTrick,
} from 'types/AlgoliaResults';

type Props = {
  query: string;
  sortMethod: string;
  searchClient: SearchClient;
  locale: string;
  recipeEnv: string;
  SEARCH_PAGE_SIZE: number;
  loadFacets: boolean;
  onSetAvailableFilters: (availableFilters: FilterTopic[]) => void;
  page: number;
  searchPageLabels: Record<string, string>;
  currentSearchCategory: string;
  searchCategories: Record<string, string>;
  appliedFilters?: string[][];
  searchItemLabels?: Record<string, string>;
  pathTranslations: PathTranslations;
};

type ReturnType = {
  totalResults: number;
  searchResults: SearchResultCard[];
};

/**
 * receive search result depending on category
 */
export const getSearchResults = async ({
  pathTranslations,
  query,
  sortMethod,
  locale,
  recipeEnv,
  searchClient,
  SEARCH_PAGE_SIZE,
  page,
  searchPageLabels,
  loadFacets,
  onSetAvailableFilters,
  currentSearchCategory,
  searchCategories,
  appliedFilters: requestedFilters,
  searchItemLabels,
}: Props): Promise<ReturnType> => {
  const appliedFilters =
    searchCategories[currentSearchCategory] === searchCategories.PRODUCTS
      ? requestedFilters
          ?.map((group) =>
            group.filter((filter) => !/^-?(brands|subcategories)\.name:/.test(filter)),
          )
          .filter((group) => group.length)
      : requestedFilters;
  const indexName = SearchService.getSortedIndexName(
    currentSearchCategory,
    recipeEnv,
    locale,
    sortMethod,
  );

  if (
    loadFacets &&
    (searchCategories[currentSearchCategory] === searchCategories.ALL ||
      searchCategories[currentSearchCategory] === searchCategories.ARTICLES)
  ) {
    // Global search has no filters
    onSetAvailableFilters([]);
  }

  if (loadFacets && searchCategories[currentSearchCategory] === searchCategories.RECIPES) {
    const response: any = await searchClient.searchSingleIndex({
      indexName,
      searchParams: {
        query,
        facetFilters: appliedFilters,
        facets: [
          'menu_types',
          'preparation_time',
          'servings',
          'difficulty',
          'diet_types',
          'seasons.title',
          'occasions',
          'rating_effective',
          'preparation_types',
          'category',
        ],
        clickAnalytics: true,
      },
    });

    const indexFilters = SearchService.updateFilters(
      response.facets ?? {},
      appliedFilters ?? [],
      searchPageLabels,
      searchItemLabels ?? {},
    );
    onSetAvailableFilters(indexFilters);
  }

  if (loadFacets && searchCategories[currentSearchCategory] === searchCategories.TIPS) {
    const response: any = await searchClient.searchSingleIndex({
      indexName,
      searchParams: {
        query,
        facetFilters: appliedFilters,
        facets: ['category'],
        clickAnalytics: true,
      },
    });

    const indexFilters = SearchService.updateFilters(
      response.facets || {},
      appliedFilters ?? [],
      searchPageLabels,
      searchItemLabels ?? {},
    );
    onSetAvailableFilters(indexFilters);
  }

  if (loadFacets && searchCategories[currentSearchCategory] === searchCategories.PRODUCTS) {
    const response: any = await searchClient.searchSingleIndex({
      indexName,
      searchParams: {
        query,
        facetFilters: appliedFilters,
        facets: ['categories.name', 'diet_list', 'meta_keywords'],
        clickAnalytics: true,
      },
    });
    const indexFilters = SearchService.updateFilters(
      response.facets || {},
      appliedFilters ?? [],
      searchPageLabels,
      searchItemLabels ?? {},
    );
    onSetAvailableFilters(indexFilters);
  }

  const response: any = await searchClient.searchSingleIndex({
    indexName,
    searchParams: {
      query,
      ...(searchCategories[currentSearchCategory] !== searchCategories.ALL && {
        facetFilters: appliedFilters,
      }),
      page,
      hitsPerPage: SEARCH_PAGE_SIZE,
      clickAnalytics: true,
    },
  });

  /**
   * map results depending on search category and return them
   */

  // recipes return
  if (searchCategories[currentSearchCategory] === searchCategories.RECIPES) {
    return {
      totalResults: response.nbHits,
      searchResults: (response.hits as FormattedRecipes[]).map((recipe) =>
        mapAlgoliaRecipeToSearchCard(recipe, searchPageLabels, pathTranslations),
      ),
    };
  }

  // tips and tricks return
  if (searchCategories[currentSearchCategory] === searchCategories.TIPS) {
    return {
      totalResults: response.nbHits,
      searchResults: (response.hits as FormattedTipsAndTrick[]).map((tip) =>
        mapAlgoliaTipsToSearchCard(tip, searchPageLabels, pathTranslations),
      ),
    };
  }

  // products return
  if (searchCategories[currentSearchCategory] === searchCategories.PRODUCTS) {
    return {
      totalResults: response.nbHits,
      searchResults: (response.hits as FormattedProduct[]).map((product) =>
        mapAlogliaProductToSearchCard(product, searchPageLabels, pathTranslations),
      ),
    };
  }

  // articles return
  if (searchCategories[currentSearchCategory] === searchCategories.ARTICLES) {
    return {
      totalResults: response.nbHits,
      searchResults: (response.hits as FormattedArticle[]).map((article) =>
        mapAlogliaArticleToSearchCard(article, searchPageLabels, pathTranslations),
      ),
    };
  }

  // no category return
  return {
    totalResults: response.nbHits,
    searchResults: mapGlobalSearchResultsToCards(response.hits, searchPageLabels, pathTranslations),
  };
};
